'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, MessageCircle, Bot, Heart, Calendar, Settings, Search, Filter, Send, MoreHorizontal, ThumbsUp, MessageSquare, Share2, Save, Pin, Volume2, VolumeX, Smile, Paperclip, Image, Play, MapPin, Clock, AlertCircle, CheckCircle, Users2, BookOpen, Radio, Star, Award, Flame, Camera } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { format, formatDistanceToNow } from 'date-fns';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import AskPastorWidget from '@/components/community/AskPastorWidget';
import LiveActivityBadge from '@/components/community/LiveActivityBadge';

// Import community service
import { fetchPosts, createPost, addReaction, addComment, subscribeToPostUpdates, fetchPrayerRequests, createPrayerRequest, prayForRequest, subscribeToPrayerUpdates, subscribeToPrayerInteractions, fetchLiveViewerCounts, subscribeToViewerCounts } from '@/lib/services/communityService';

// Mock data for demonstration
const mockCurrentUser = {
  id: 'user-1',
  name: 'Pastor Denis Kiwewesi',
  avatar: '/api/placeholder/40/40',
  alt: 'Pastor Denis Kiwewesi - Senior Pastor at Christ Faculty Ministry',
  roles: ['Admin', 'Pastor'],
  bio: 'Senior Pastor at Christ Faculty Ministry, dedicated to spreading God\'s love and freedom.',
  badges: ['Faith Builder', 'Prayer Warrior', 'Teacher'],
  streak: 12,
  points: 2450
};

const mockPosts = [
  {
    id: 'post-1',
    author: {
      id: 'user-2',
      name: 'Grace Nakato',
      avatar: '/api/placeholder/40/40',
      alt: 'Grace Nakato - Community member sharing testimony',
      roles: ['Member']
    },
    type: 'testimony',
    content: 'Praise God! I want to testify about God\'s goodness in my life. After months of seeking employment, the Lord has opened a door for me at a Christian organization. His timing is perfect! 🙏',
    media: [],
    tags: ['#testimony', '#employment', '#Godsgoodness'],
    category: 'Testimonies',
    reactions: {
      amen: 15,
      prayer: 8,
      heart: 23
    },
    comments: 12,
    shares: 5,
    createdAt: new Date('2025-11-12T10:30:00'),
    isPinned: false,
    isFollowing: false
  },
  {
    id: 'post-2',
    author: {
      id: 'user-3',
      name: 'Samuel Mukasa',
      avatar: '/api/placeholder/40/40',
      alt: 'Samuel Mukasa - Youth leader requesting prayer',
      roles: ['Youth Leader']
    },
    type: 'prayer_request',
    content: 'Dear family, please pray for my mother who is battling cancer. The doctors say the treatment is going well, but we believe in the power of prayer for complete healing. Thank you for standing with us.',
    media: [],
    tags: ['#prayer', '#healing', '#family'],
    category: 'Prayer Requests',
    reactions: {
      amen: 45,
      prayer: 67,
      heart: 32
    },
    comments: 28,
    shares: 12,
    createdAt: new Date('2025-11-12T08:15:00'),
    isPinned: true,
    isFollowing: true
  },
  {
    id: 'post-3',
    author: mockCurrentUser,
    type: 'teaching',
    content: 'Today\'s reflection: "Faith is not the belief that God will do what you want. It is the belief that God will do what is right." - Max Lucado. Trust in His perfect plan even when you don\'t understand. 📖✨',
    media: [
      {
        type: 'image',
        url: '/api/placeholder/400/300',
        alt: 'Inspirational quote about faith with scripture background'
      }
    ],
    tags: ['#faith', '#trust', '#dailydevotion'],
    category: 'Teachings',
    reactions: {
      amen: 89,
      prayer: 23,
      heart: 156
    },
    comments: 34,
    shares: 45,
    createdAt: new Date('2025-11-12T06:00:00'),
    isPinned: false,
    isFollowing: false
  }
];

const mockPrayerRequests = [
  {
    id: 'prayer-1',
    author: {
      id: 'user-4',
      name: 'Mary Nakigozi',
      avatar: '/api/placeholder/40/40',
      alt: 'Mary Nakigozi - Community member requesting prayer for family'
    },
    title: 'Prayer for Family Restoration',
    body: 'Please pray for reconciliation in my family. There has been division for years, and we need God\'s intervention for healing and unity.',
    category: 'Family',
    isAnonymous: false,
    prayedCount: 24,
    status: 'active',
    createdAt: new Date('2025-11-11T15:30:00'),
    isFollowing: true,
    updates: []
  },
  {
    id: 'prayer-2',
    author: {
      id: 'anonymous',
      name: 'Anonymous',
      avatar: '/api/placeholder/40/40',
      alt: 'Anonymous prayer request for healing'
    },
    title: 'Healing and Recovery',
    body: 'Please pray for my healing. I\'ve been struggling with a chronic condition and need God\'s touch for complete restoration.',
    category: 'Healing',
    isAnonymous: true,
    prayedCount: 67,
    status: 'active',
    createdAt: new Date('2025-11-11T12:00:00'),
    isFollowing: false,
    updates: []
  }
];

const mockEvents = [
  {
    id: 'event-1',
    title: 'Sunday Morning Service',
    description: 'Join us for our weekly Sunday morning worship service filled with praise, worship, and the Word of God.',
    startDate: new Date('2025-11-17T10:00:00'),
    endDate: new Date('2025-11-17T12:30:00'),
    location: 'Main Sanctuary',
    type: 'Service',
    tags: ['worship', 'sermon', 'fellowship'],
    rsvpCount: 250,
    isRsvped: true,
    streamUrl: 'https://stream.church.com/sunday',
    image: '/api/placeholder/300/200',
    alt: 'Sunday morning worship service in the main sanctuary with congregation'
  },
  {
    id: 'event-2',
    title: 'Trumpet Bible School',
    description: 'Intensive Bible study program for spiritual growth and ministry preparation.',
    startDate: new Date('2025-11-19T18:00:00'),
    endDate: new Date('2025-11-19T20:00:00'),
    location: 'Education Wing',
    type: 'Bible School',
    tags: ['education', 'ministry', 'growth'],
    rsvpCount: 45,
    isRsvped: false,
    streamUrl: null,
    image: '/api/placeholder/300/200',
    alt: 'Bible study classroom with students engaged in learning'
  },
  {
    id: 'event-3',
    title: 'Deliverance Service',
    description: 'Special service focused on spiritual breakthrough and freedom through prayer and ministry.',
    startDate: new Date('2025-11-22T19:00:00'),
    endDate: new Date('2025-11-22T21:30:00'),
    location: 'Prayer Hall',
    type: 'Deliverance Service',
    tags: ['deliverance', 'prayer', 'breakthrough'],
    rsvpCount: 180,
    isRsvped: true,
    streamUrl: 'https://stream.church.com/deliverance',
    image: '/api/placeholder/300/200',
    alt: 'Prayer hall prepared for deliverance service with altar and prayer space'
  }
];

const mockGroups = [
  {
    id: 'group-1',
    name: 'Marrieds Fellowship',
    description: 'A support group for married couples focusing on strengthening relationships through biblical principles.',
    memberCount: 85,
    category: 'Marrieds',
    isJoined: true,
    leader: 'Pastor David & Ruth',
    lastActivity: new Date('2025-11-12T14:00:00'),
    image: '/api/placeholder/200/150',
    alt: 'Married couples fellowship group meeting in church hall'
  },
  {
    id: 'group-2',
    name: 'Youth Ministry',
    description: 'Empowering young people to live bold lives for Christ through fellowship, service, and discipleship.',
    memberCount: 120,
    category: 'Youth',
    isJoined: false,
    leader: 'Pastor Samuel',
    lastActivity: new Date('2025-11-12T16:30:00'),
    image: '/api/placeholder/200/150',
    alt: 'Youth group engaged in worship and fellowship activities'
  },
  {
    id: 'group-3',
    name: 'Ministers\' Garage',
    description: 'A space for ministry leaders to connect, share experiences, and support one another in service.',
    memberCount: 35,
    category: 'Ministers',
    isJoined: true,
    leader: 'Pastor Denis',
    lastActivity: new Date('2025-11-12T11:15:00'),
    image: '/api/placeholder/200/150',
    alt: 'Ministry leaders in discussion during ministers garage meeting'
  }
];

const mockChatMessages = [
  {
    id: 'msg-1',
    user: {
      id: 'user-5',
      name: 'Faith Nakiwala',
      avatar: '/api/placeholder/30/30',
      alt: 'Faith Nakiwala participating in live chat'
    },
    message: 'Pastor Denis, how do we maintain faith during difficult seasons?',
    timestamp: new Date('2025-11-12T19:00:00'),
    isAnswering: true
  },
  {
    id: 'msg-2',
    user: mockCurrentUser,
    message: 'Thank you Faith for that question. Remember that faith grows strongest in the storm. God uses our difficulties to deepen our trust in Him.',
    timestamp: new Date('2025-11-12T19:02:00'),
    isAnswering: false
  }
];

const mockResources = [
  {
    id: 'res-1',
    title: 'Digital Books Library',
    description: 'Access thousands of Christian books, devotionals, and study materials.',
    type: 'Library',
    url: '/digital-books-library',
    icon: '📚',
    category: 'Education'
  },
  {
    id: 'res-2',
    title: 'Audio Library',
    description: 'Listen to sermons, teachings, and Christian music from our collection.',
    type: 'Audio',
    url: '/audio-book-library',
    icon: '🎧',
    category: 'Media'
  },
  {
    id: 'res-3',
    title: 'Live Radio/TV',
    description: 'Watch and listen to our live broadcasts and programs.',
    type: 'Streaming',
    url: '/live-streams-hub',
    icon: '📺',
    category: 'Media'
  },
  {
    id: 'res-4',
    title: 'Donation Portal',
    description: 'Support the ministry through secure online giving.',
    type: 'Donation',
    url: '/donate',
    icon: '💝',
    category: 'Giving'
  }
];

// Component definitions
const PostCard = ({ post, onReact, onComment, onShare, onSave, onReport, onPin }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [timeAgo, setTimeAgo] = useState('');
  const [isClient, setIsClient] = useState(false);
  const [newReactions, setNewReactions] = useState(0);
  const [newCommentsCount, setNewCommentsCount] = useState(0);
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });

  const formatTimeAgo = (date) => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Fix hydration mismatch by ensuring client-side rendering for dynamic content
  useEffect(() => {
    setIsClient(true);
    setTimeAgo(formatTimeAgo(post?.createdAt));
    
    // Update time every minute to keep it current
    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(post?.createdAt));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [post?.createdAt]);

  // Simulate new reactions/comments (in real app, would come from subscriptions)
  useEffect(() => {
    const reactionTimer = setInterval(() => {
      if (Math.random() > 0.7) {
        setNewReactions(prev => prev + 1);
        setTimeout(() => setNewReactions(0), 3000);
      }
    }, 5000);

    const commentTimer = setInterval(() => {
      if (Math.random() > 0.8) {
        setNewCommentsCount(prev => prev + 1);
        setTimeout(() => setNewCommentsCount(0), 3000);
      }
    }, 7000);

    return () => {
      clearInterval(reactionTimer);
      clearInterval(commentTimer);
    };
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden relative"
    >
      {/* Live activity indicator for trending posts */}
      {post?.isPinned && (
        <div className="absolute top-4 right-4 z-10">
          <LiveActivityBadge
            type="activity"
            count={post?.reactions?.amen + post?.reactions?.prayer + post?.reactions?.heart}
            showPulse={true}
            variant="live"
            size="sm" />

        </div>
      )}
      {/* Post Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src={post?.author?.avatar} 
              alt={post?.author?.alt}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-900">{post?.author?.name}</h3>
                {post?.author?.roles?.includes('Pastor') && (
                  <span className="bg-[#4B3381] text-white px-2 py-1 rounded-full text-xs">
                    Pastor
                  </span>
                )}
                {post?.author?.roles?.includes('Admin') && (
                  <span className="bg-[#F97A60] text-white px-2 py-1 rounded-full text-xs">
                    Admin
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>{isClient ? timeAgo : 'just now'}</span>
                <span>•</span>
                <span className="capitalize">{post?.category}</span>
                {post?.isPinned && (
                  <>
                    <span>•</span>
                    <Pin className="w-4 h-4 text-[#4B3381]" />
                    <span>Pinned</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <MoreHorizontal className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
      {/* Post Content */}
      <div className="p-4">
        <p className="text-gray-800 leading-relaxed mb-3">{post?.content}</p>
        
        {/* Tags */}
        {post?.tags && post?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {post?.tags?.map((tag, index) => (
              <span 
                key={index}
                className="text-[#4B3381] text-sm bg-[#4B3381]/10 px-2 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Media */}
        {post?.media && post?.media?.length > 0 && (
          <div className="mb-4">
            {post?.media?.map((item, index) => (
              <div key={index} className="rounded-lg overflow-hidden">
                {item?.type === 'image' && (
                  <img 
                    src={item?.url} 
                    alt={item?.alt}
                    className="w-full h-64 object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Reactions Summary with live indicators */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <div className="flex items-center space-x-4">
            <LiveActivityBadge
              type="prayers"
              count={post?.reactions?.amen + post?.reactions?.prayer + post?.reactions?.heart}
              showNewIndicator={newReactions > 0}
              newCount={newReactions}
              size="sm"
              variant="default"
              className="mr-2" />

            <LiveActivityBadge
              type="comments"
              count={post?.comments}
              showNewIndicator={newCommentsCount > 0}
              newCount={newCommentsCount}
              size="sm"
              variant="info"
              onRefresh={() => setShowComments(true)} />

            <span>{post?.shares} shares</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-1">
            <button 
              onClick={() => onReact?.(post?.id, 'amen')}
              className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <span className="text-lg">🙏</span>
              <span className="text-sm font-medium">Amen</span>
              <span className="text-xs text-gray-500">({post?.reactions?.amen})</span>
            </button>
            <button 
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-medium">Comment</span>
            </button>
            <button 
              onClick={() => onShare?.(post?.id)}
              className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              <span className="text-sm font-medium">Share</span>
            </button>
          </div>
          <div className="flex items-center space-x-1">
            <button 
              onClick={() => onSave?.(post?.id)}
              className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
            </button>
            {post?.isFollowing && (
              <span className="bg-[#4B3381] text-white px-2 py-1 rounded-full text-xs">
                Following
              </span>
            )}
          </div>
        </div>

        {/* Comments Section */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-100"
            >
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {/* Mock comments */}
                <div className="flex space-x-3">
                  <img 
                    src="/api/placeholder/30/30" 
                    alt="Commenter profile"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex-1 bg-gray-50 rounded-lg p-3">
                    <div className="font-medium text-sm text-gray-900">Sarah Namukose</div>
                    <div className="text-sm text-gray-700">Amen! God is faithful in all circumstances.</div>
                  </div>
                </div>
              </div>
              
              {/* Add Comment */}
              <div className="flex space-x-3">
                <img 
                  src={mockCurrentUser?.avatar} 
                  alt={mockCurrentUser?.alt}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e?.target?.value)}
                    placeholder="Write a comment..."
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
                    rows={2}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center space-x-2">
                      <button className="p-1 hover:bg-gray-100 rounded">
                        <Smile className="w-4 h-4 text-gray-500" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 rounded">
                        <Image className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    <button 
                      onClick={() => {
                        if (newComment?.trim()) {
                          onComment?.(post?.id, newComment);
                          setNewComment('');
                        }
                      }}
                      disabled={!newComment?.trim()}
                      className="bg-[#4B3381] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

PostCard.propTypes = {
  post: PropTypes?.object?.isRequired,
  onReact: PropTypes?.func,
  onComment: PropTypes?.func,
  onShare: PropTypes?.func,
  onSave: PropTypes?.func,
  onReport: PropTypes?.func,
  onPin: PropTypes?.func
};

const PrayerCard = ({ prayer, onPrayed, onFollow }) => {
  const [timeAgo, setTimeAgo] = useState('');
  const [isClient, setIsClient] = useState(false);
  const [newPrayers, setNewPrayers] = useState(0);

  const formatTimeAgo = (date) => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Fix hydration mismatch for prayer cards
  useEffect(() => {
    setIsClient(true);
    setTimeAgo(formatTimeAgo(prayer?.createdAt));
    
    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(prayer?.createdAt));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [prayer?.createdAt]);

  // Simulate new prayers (in real app, would come from subscriptions)
  useEffect(() => {
    const timer = setInterval(() => {
      if (Math.random() > 0.75) {
        setNewPrayers(prev => prev + 1);
        setTimeout(() => setNewPrayers(0), 3000);
      }
    }, 6000);

    return () => clearInterval(timer);
  }, []);

  const getCategoryColor = (category) => {
    const colors = {
      'Healing': 'bg-green-100 text-green-800',
      'Family': 'bg-blue-100 text-blue-800',
      'Provision': 'bg-yellow-100 text-yellow-800',
      'Nation': 'bg-purple-100 text-purple-800'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 relative">
      {/* Live prayer activity indicator */}
      <div className="absolute top-4 right-4">
        <LiveActivityBadge
          type="prayers"
          count={prayer?.prayedCount}
          showPulse={prayer?.prayedCount > 50}
          showNewIndicator={newPrayers > 0}
          newCount={newPrayers}
          size="sm"
          variant={prayer?.prayedCount > 100 ? 'success' : 'default'} />

      </div>

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <img 
            src={prayer?.author?.avatar} 
            alt={prayer?.author?.alt}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">
              {prayer?.isAnonymous ? 'Anonymous' : prayer?.author?.name}
            </h3>
            <p className="text-sm text-gray-500">{isClient ? timeAgo : 'just now'}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(prayer?.category)}`}>
          {prayer?.category}
        </span>
      </div>
      <h4 className="font-semibold text-gray-900 mb-2">{prayer?.title}</h4>
      <p className="text-gray-700 mb-4">{prayer?.body}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => onPrayed?.(prayer?.id)}
            className="flex items-center space-x-2 text-[#4B3381] hover:bg-[#4B3381]/10 px-3 py-2 rounded-lg transition-colors">

            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">I Prayed</span>
            <LiveActivityBadge
              type="prayers"
              count={prayer?.prayedCount}
              size="sm"
              variant="success"
              className="ml-1" />

          </button>
          <button 
            onClick={() => onFollow?.(prayer?.id)}
            className={`text-sm px-3 py-2 rounded-lg transition-colors ${
              prayer?.isFollowing 
                ? 'bg-[#4B3381] text-white' 
                : 'text-[#4B3381] hover:bg-[#4B3381]/10'
            }`}
          >
            {prayer?.isFollowing ? 'Following' : 'Follow Updates'}
          </button>
        </div>
      </div>
    </div>
  );
};

PrayerCard.propTypes = {
  prayer: PropTypes?.object?.isRequired,
  onPrayed: PropTypes?.func,
  onFollow: PropTypes?.func
};

const EventCard = ({ event, onRSVP }) => {
  const formatEventDate = (date) => {
    return format(date, 'MMM dd, yyyy • h:mm a');
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <img 
        src={event?.image} 
        alt={event?.alt}
        className="w-full h-48 object-cover"
      />
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="bg-[#F97A60] text-white px-2 py-1 rounded-full text-xs font-medium">
            {event?.type}
          </span>
          {event?.streamUrl && (
            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
              <Radio className="w-3 h-3 mr-1" />
              Live Stream
            </span>
          )}
        </div>

        <h3 className="font-semibold text-gray-900 mb-2">{event?.title}</h3>
        <p className="text-gray-600 text-sm mb-3">{event?.description}</p>

        <div className="space-y-2 text-sm text-gray-500 mb-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{formatEventDate(event?.startDate)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4" />
            <span>{event?.location}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4" />
            <span>{event?.rsvpCount} attending</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button 
            onClick={() => onRSVP?.(event?.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              event?.isRsvped
                ? 'bg-[#4B3381] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {event?.isRsvped ? 'Going' : 'RSVP'}
          </button>
          
          {event?.streamUrl && (
            <button className="flex items-center space-x-2 text-[#F97A60] hover:bg-[#F97A60]/10 px-3 py-2 rounded-lg transition-colors">
              <Play className="w-4 h-4" />
              <span className="text-sm font-medium">Watch Live</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

EventCard.propTypes = {
  event: PropTypes?.object?.isRequired,
  onRSVP: PropTypes?.func
};

const GroupCard = ({ group, onJoin }) => {
  const [lastActivityTime, setLastActivityTime] = useState('');
  const [isClient, setIsClient] = useState(false);

  const formatLastActivity = (date) => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Fix hydration mismatch for group cards
  useEffect(() => {
    setIsClient(true);
    setLastActivityTime(formatLastActivity(group?.lastActivity));
    
    const interval = setInterval(() => {
      setLastActivityTime(formatLastActivity(group?.lastActivity));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [group?.lastActivity]);

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <img 
        src={group?.image} 
        alt={group?.alt}
        className="w-full h-32 object-cover"
      />
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{group?.name}</h3>
          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
            {group?.category}
          </span>
        </div>

        <p className="text-gray-600 text-sm mb-3">{group?.description}</p>

        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
          <div className="flex items-center space-x-2">
            <Users2 className="w-4 h-4" />
            <span>{group?.memberCount} members</span>
          </div>
          <span>Active {isClient ? lastActivityTime : 'recently'}</span>
        </div>

        <p className="text-sm text-gray-600 mb-4">Led by {group?.leader}</p>

        <button 
          onClick={() => onJoin?.(group?.id)}
          className={`w-full py-2 rounded-lg font-medium transition-colors ${
            group?.isJoined
              ? 'bg-[#4B3381] text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {group?.isJoined ? 'Joined' : 'Join Group'}
        </button>
      </div>
    </div>
  );
};

GroupCard.propTypes = {
  group: PropTypes?.object?.isRequired,
  onJoin: PropTypes?.func
};

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      type: 'ai',
      content: 'Welcome! I am Pastor Denis AI Assistant. I\'m here to provide biblical guidance, answer questions about faith, and help you with spiritual matters. How can I assist you today?',
      timestamp: new Date(),
      sources: []
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const messagesEndRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'lg', name: 'Luganda', flag: '🇺🇬' },
    { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
    { code: 'fr', name: 'French', flag: '🇫🇷' }
  ];

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage?.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Thank you for your question. This is a simulated response from Pastor Denis AI. In a real implementation, this would connect to OpenAI API with biblical context and church resources. Your question about "${inputMessage}" would be answered with scriptural references and pastoral guidance.`,
        timestamp: new Date(),
        sources: [
          { title: 'Romans 8:28', url: '/bible/romans/8/28' },
          { title: 'Faith & Trust Sermon', url: '/sermons/faith-trust' }
        ]
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 2000);
  };

  const handleFeedback = (messageId, type) => {
    console.log(`Feedback ${type} for message ${messageId}`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Language Selector */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Ask Pastor Denis (AI)</h3>
          <select 
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e?.target?.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1"
          >
            {languages?.map(lang => (
              <option key={lang?.code} value={lang?.code}>
                {lang?.flag} {lang?.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map(message => (
          <div 
            key={message?.id} 
            className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
              message?.type === 'user' ?'bg-[#4B3381] text-white' :'bg-white border border-gray-200'
            }`}>
              {message?.type === 'ai' && (
                <div className="flex items-center space-x-2 mb-2">
                  <Bot className="w-4 h-4 text-[#4B3381]" />
                  <span className="text-sm font-medium text-[#4B3381]">Pastor Denis AI</span>
                </div>
              )}
              
              <p className="text-sm">{message?.content}</p>
              
              {message?.sources && message?.sources?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Sources:</p>
                  {message?.sources?.map((source, index) => (
                    <a 
                      key={index} 
                      href={source?.url}
                      className="text-xs text-[#4B3381] hover:underline block"
                    >
                      📖 {source?.title}
                    </a>
                  ))}
                </div>
              )}
              
              {message?.type === 'ai' && (
                <div className="flex items-center space-x-2 mt-2">
                  <button 
                    onClick={() => handleFeedback(message?.id, 'thumbs_up')}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ThumbsUp className="w-3 h-3 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => handleFeedback(message?.id, 'thumbs_down')}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ThumbsUp className="w-3 h-3 text-gray-400 rotate-180" />
                  </button>
                </div>
              )}
              
              <p className="text-xs text-gray-400 mt-1">
                {format(message?.timestamp, 'h:mm a')}
              </p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 max-w-xs">
              <div className="flex items-center space-x-2">
                <Bot className="w-4 h-4 text-[#4B3381]" />
                <span className="text-sm text-gray-500">Pastor Denis AI is typing...</span>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
            placeholder="Ask about faith, prayer, or biblical guidance..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
            disabled={isLoading}
          />
          <button 
            onClick={handleSendMessage}
            disabled={!inputMessage?.trim() || isLoading}
            className="bg-[#4B3381] text-white px-4 py-2 rounded-lg hover:bg-[#4B3381]/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        
        {/* Quick Questions */}
        <div className="mt-2 flex flex-wrap gap-2">
          {[
            'How to pray effectively?',
            'Bible study tips',
            'Dealing with anxiety',
            'Finding God\'s purpose'
          ]?.map((question, index) => (
            <button 
              key={index}
              onClick={() => setInputMessage(question)}
              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
            >
              {question}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const LiveChat = () => {
  const [messages, setMessages] = useState(mockChatMessages);
  const [newMessage, setNewMessage] = useState('');
  const [isLive, setIsLive] = useState(true);
  const [viewerCount, setViewerCount] = useState(234);
  const [isMuted, setIsMuted] = useState(false);
  const [newViewers, setNewViewers] = useState(0);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Simulate viewer count changes
  useEffect(() => {
    const interval = setInterval(() => {
      const change = Math.floor(Math.random() * 10) - 5;
      setViewerCount(prev => Math.max(0, prev + change));
      if (change > 0) {
        setNewViewers(change);
        setTimeout(() => setNewViewers(0), 2000);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;

    const message = {
      id: Date.now(),
      user: mockCurrentUser,
      message: newMessage,
      timestamp: new Date(),
      isAnswering: false
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Live Session Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <LiveActivityBadge
              type="viewers"
              count={viewerCount}
              showPulse={isLive}
              showNewIndicator={newViewers > 0}
              newCount={newViewers}
              variant={isLive ? 'live' : 'default'}
              label="viewers" />

            <h3 className="font-semibold text-gray-900">
              {isLive ? 'Live Q&A with Pastor Denis' : 'Chat Room'}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setIsMuted(!isMuted)}
              className={`p-2 rounded-lg ${isMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>

              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button className="p-2 bg-gray-100 text-gray-600 rounded-lg">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages?.map(message => (
          <div key={message?.id} className={`${message?.isAnswering ? 'bg-yellow-50 border border-yellow-200 rounded-lg p-2' : ''}`}>
            <div className="flex items-start space-x-3">
              <img 
                src={message?.user?.avatar} 
                alt={message?.user?.alt}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-sm text-gray-900">{message?.user?.name}</span>
                  {message?.user?.roles?.includes('Pastor') && (
                    <span className="bg-[#4B3381] text-white px-2 py-1 rounded-full text-xs">
                      Pastor
                    </span>
                  )}
                  {message?.isAnswering && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                      Answering Now
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {format(message?.timestamp, 'h:mm a')}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mt-1">{message?.message}</p>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
            placeholder="Send a message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
          />
          <button className="p-2 hover:bg-gray-100 rounded-lg">
            <Smile className="w-4 h-4 text-gray-500" />
          </button>
          <button 
            onClick={handleSendMessage}
            disabled={!newMessage?.trim()}
            className="bg-[#4B3381] text-white px-4 py-2 rounded-lg hover:bg-[#4B3381]/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CommunityHub = () => {
  const [activeTab, setActiveTab] = useState('feed');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Latest');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({
    type: 'text',
    content: '',
    category: 'General'
  });
  
  // Real-time state
  const [posts, setPosts] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [liveViewerCounts, setLiveViewerCounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Activity tracking
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [newPrayersCount, setNewPrayersCount] = useState(0);
  
  // Subscription refs
  const postsSubscriptionRef = useRef(null);
  const prayersSubscriptionRef = useRef(null);
  const viewerCountsSubscriptionRef = useRef(null);

  const tabs = [
    { id: 'feed', label: 'Feed', icon: Users },
    { id: 'ask-pastor', label: 'Ask Pastor', icon: Bot },
    { id: 'live-chat', label: 'Live Chat', icon: MessageCircle },
    { id: 'prayer-wall', label: 'Prayer Wall', icon: Heart },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'groups', label: 'Groups', icon: Users2 },
    { id: 'resources', label: 'Resources', icon: BookOpen }
  ];

  const filters = ['Latest', 'Top', 'Testimonies', 'Prayer Requests', 'Teachings'];
  const postTypes = ['text', 'testimony', 'prayer_request', 'praise_report', 'question'];
  const postCategories = ['General', 'Singles', 'Couples', 'Youth', 'Bible School', 'Ministers'];

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Setup real-time subscriptions
  useEffect(() => {
    setupRealtimeSubscriptions();
    
    return () => {
      // Cleanup subscriptions
      if (postsSubscriptionRef?.current) {
        postsSubscriptionRef?.current?.unsubscribe();
      }
      if (prayersSubscriptionRef?.current) {
        prayersSubscriptionRef?.current?.unsubscribe();
      }
      if (viewerCountsSubscriptionRef?.current) {
        viewerCountsSubscriptionRef?.current?.unsubscribe();
      }
    };
  }, []);

  // Simulate active users (in real app, would come from presence system)
  useEffect(() => {
    const interval = setInterval(() => {
      setTotalActiveUsers(Math.floor(Math.random() * 50) + 150);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [postsData, prayersData, viewerData] = await Promise.all([
        fetchPosts(),
        fetchPrayerRequests(),
        fetchLiveViewerCounts()
      ]);
      
      setPosts(postsData);
      setPrayerRequests(prayersData);
      setLiveViewerCounts(viewerData);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load community data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to post updates
    postsSubscriptionRef.current = subscribeToPostUpdates((payload) => {
      if (payload?.eventType === 'INSERT') {
        setPosts(prev => [payload?.new, ...prev]);
        setNewPostsCount(prev => prev + 1);
        setTimeout(() => setNewPostsCount(0), 3000);
      } else if (payload?.eventType === 'UPDATE') {
        setPosts(prev => prev?.map(p => p?.id === payload?.new?.id ? payload?.new : p));
      } else if (payload?.eventType === 'DELETE') {
        setPosts(prev => prev?.filter(p => p?.id !== payload?.old?.id));
      }
    });

    // Subscribe to prayer request updates
    prayersSubscriptionRef.current = subscribeToPrayerUpdates((payload) => {
      if (payload?.eventType === 'INSERT') {
        setPrayerRequests(prev => [payload?.new, ...prev]);
        setNewPrayersCount(prev => prev + 1);
        setTimeout(() => setNewPrayersCount(0), 3000);
      } else if (payload?.eventType === 'UPDATE') {
        setPrayerRequests(prev => prev?.map(p => p?.id === payload?.new?.id ? payload?.new : p));
      }
    });

    // Subscribe to prayer interaction updates (for live pray counts)
    subscribeToPrayerInteractions((payload) => {
      if (payload?.eventType === 'INSERT') {
        setPrayerRequests(prev => prev?.map(p => {
          if (p?.id === payload?.new?.prayer_id) {
            return { ...p, prayedCount: (p?.prayedCount || 0) + 1 };
          }
          return p;
        }));
      }
    });

    // Subscribe to live viewer count updates
    viewerCountsSubscriptionRef.current = subscribeToViewerCounts((payload) => {
      fetchLiveViewerCounts()?.then(setLiveViewerCounts);
    });
  };

  const handleCreatePost = async () => {
    if (!newPost?.content?.trim()) return;

    try {
      await createPost({
        content: newPost?.content,
        type: newPost?.type,
        category: newPost?.category
      });
      
      setShowCreatePost(false);
      setNewPost({ type: 'text', content: '', category: 'General' });
    } catch (err) {
      console.error('Error creating post:', err);
      alert('Failed to create post. Please try again.');
    }
  };

  const handleReact = async (postId, reactionType) => {
    try {
      await addReaction({ postId, reactionType });
    } catch (err) {
      console.error('Error adding reaction:', err);
    }
  };

  const handleComment = async (postId, content) => {
    try {
      await addComment({ postId, content });
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const handlePrayForRequest = async (prayerId) => {
    try {
      await prayForRequest(prayerId);
    } catch (err) {
      console.error('Error praying for request:', err);
    }
  };

  const handleCreatePrayerRequest = async (formData) => {
    try {
      await createPrayerRequest(formData);
    } catch (err) {
      console.error('Error creating prayer request:', err);
      alert('Failed to create prayer request. Please try again.');
    }
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B3381]"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={loadInitialData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      );
    }

    switch (activeTab) {
      case 'feed':
        return (
          <div className="space-y-6">
            {/* Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex flex-wrap gap-2">
                  {filters?.map(filter => (
                    <button 
                      key={filter}
                      onClick={() => setActiveFilter(filter)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeFilter === filter 
                          ? 'bg-[#4B3381] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search posts..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            {/* Posts */}
            {posts?.length > 0 ? (
              posts?.map(post => (
                <PostCard 
                  key={post?.id} 
                  post={post}
                  onReact={handleReact}
                  onComment={handleComment}
                  onShare={(postId) => console.log('Share:', postId)}
                  onSave={(postId) => console.log('Save:', postId)}
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No posts yet. Be the first to share!</p>
              </div>
            )}
          </div>
        );

      case 'ask-pastor':
        return (
          <div className="h-[600px]">
            <AskPastorWidget />
          </div>
        );

      case 'live-chat':
        return <LiveChat />;

      case 'prayer-wall':
        return (
          <div className="space-y-6">
            {/* Prayer Request Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Submit Prayer Request</h3>
              <form className="space-y-4">
                <input
                  type="text"
                  placeholder="Prayer request title..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
                />
                <textarea
                  placeholder="Describe your prayer request..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
                ></textarea>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <div className="flex items-center space-x-4">
                    <select className="px-3 py-2 border border-gray-300 rounded-lg">
                      <option>Healing</option>
                      <option>Family</option>
                      <option>Provision</option>
                      <option>Nation</option>
                    </select>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-gray-600">Submit anonymously</span>
                    </label>
                  </div>
                  <button 
                    type="submit"
                    className="bg-[#4B3381] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#4B3381]/90"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
            {/* Prayer Requests */}
            {prayerRequests?.length > 0 ? (
              prayerRequests?.map(prayer => (
                <PrayerCard 
                  key={prayer?.id} 
                  prayer={prayer}
                  onPrayed={handlePrayForRequest}
                  onFollow={(prayerId) => console.log('Follow:', prayerId)}
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No prayer requests yet. Share your prayer need!</p>
              </div>
            )}
          </div>
        );

      case 'events':
        return (
          <div className="space-y-6">
            {/* Calendar View Toggle */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Upcoming Events</h3>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">
                    Week View
                  </button>
                  <button className="px-3 py-2 bg-[#4B3381] text-white rounded-lg text-sm">
                    List View
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {mockEvents?.map(event => (
                <EventCard 
                  key={event?.id} 
                  event={event}
                  onRSVP={(eventId) => console.log('RSVP:', eventId)}
                />
              ))}
            </div>
          </div>
        );

      case 'groups':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {mockGroups?.map(group => (
                <GroupCard 
                  key={group?.id} 
                  group={group}
                  onJoin={(groupId) => console.log('Join group:', groupId)}
                />
              ))}
            </div>
          </div>
        );

      case 'resources':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockResources?.map(resource => (
                <div key={resource?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                  <div className="text-4xl mb-4">{resource?.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{resource?.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{resource?.description}</p>
                  <a 
                    href={resource?.url}
                    className="inline-flex items-center justify-center w-full bg-[#4B3381] text-white px-4 py-2 rounded-lg hover:bg-[#4B3381]/90 transition-colors"
                  >
                    Access {resource?.type}
                  </a>
                </div>
              ))}
            </div>
            {/* New Member Onboarding */}
            <div className="bg-gradient-to-r from-[#4B3381] to-[#F97A60] rounded-lg p-6 text-white">
              <h3 className="font-bold text-xl mb-2">New to Our Community? 🎉</h3>
              <p className="mb-4">Complete your onboarding checklist to get started!</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 mb-2" />
                  <h4 className="font-semibold">Complete Profile</h4>
                  <p className="text-sm opacity-90">Add your photo and bio</p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                  <Users className="w-6 h-6 mb-2" />
                  <h4 className="font-semibold">Join a Group</h4>
                  <p className="text-sm opacity-90">Connect with others</p>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                  <Heart className="w-6 h-6 mb-2" />
                  <h4 className="font-semibold">Share Prayer</h4>
                  <p className="text-sm opacity-90">Post your first request</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-[#4B3381] to-[#F97A60] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2">Community Hub</h1>
              <p className="text-lg opacity-90">Connect, Share, and Grow Together in Faith</p>
              
              {/* Live activity indicators */}
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <LiveActivityBadge
                  type="users"
                  count={totalActiveUsers}
                  showPulse={true}
                  variant="live"
                  label="active now"
                  size="md" />

                {newPostsCount > 0 && (
                  <LiveActivityBadge
                    type="activity"
                    count={newPostsCount}
                    showNewIndicator={true}
                    newCount={newPostsCount}
                    variant="success"
                    label="new posts"
                    size="md" />

                )}
                {newPrayersCount > 0 && (
                  <LiveActivityBadge
                    type="prayers"
                    count={newPrayersCount}
                    showNewIndicator={true}
                    newCount={newPrayersCount}
                    variant="warning"
                    label="new prayers"
                    size="md" />

                )}
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="mt-6 lg:mt-0 flex flex-wrap gap-3">
              <button 
                onClick={() => setShowCreatePost(true)}
                className="bg-white text-[#4B3381] px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center space-x-2"
              >
                <Camera className="w-4 h-4" />
                <span>New Post</span>
              </button>
              <button 
                onClick={() => setActiveTab('ask-pastor')}
                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center space-x-2"
              >
                <Bot className="w-4 h-4" />
                <span>Ask Pastor</span>
              </button>
              <button 
                onClick={() => setActiveTab('live-chat')}
                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center space-x-2"
              >
                <Radio className="w-4 h-4" />
                <span>Go Live</span>
              </button>
              <button 
                onClick={() => setActiveTab('prayer-wall')}
                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors flex items-center space-x-2"
              >
                <Heart className="w-4 h-4" />
                <span>Prayer Room</span>
              </button>
            </div>
          </div>

          {/* User Stats */}
          <div className="mt-6 flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <Flame className="w-4 h-4" />
              <span>{mockCurrentUser?.streak} day streak</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4" />
              <span>{mockCurrentUser?.points} points</span>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-4 h-4" />
              <span>{mockCurrentUser?.badges?.length} badges</span>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            {tabs?.map(tab => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-shrink-0 flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-[#4B3381] text-[#4B3381]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className={`${activeTab === 'ask-pastor' || activeTab === 'live-chat' ? 'h-96' : ''}`}>
          {renderTabContent()}
        </div>
      </div>
      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreatePost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreatePost(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Create New Post</h3>
                <button 
                  onClick={() => setShowCreatePost(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={(e) => { e?.preventDefault(); handleCreatePost(); }}>
                <div className="space-y-4">
                  <div className="flex space-x-4">
                    <select 
                      value={newPost?.type}
                      onChange={(e) => setNewPost(prev => ({ ...prev, type: e?.target?.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="text">General Post</option>
                      <option value="testimony">Testimony</option>
                      <option value="prayer_request">Prayer Request</option>
                      <option value="praise_report">Praise Report</option>
                      <option value="question">Question</option>
                    </select>
                    <select 
                      value={newPost?.category}
                      onChange={(e) => setNewPost(prev => ({ ...prev, category: e?.target?.value }))}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {postCategories?.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <textarea
                    value={newPost?.content}
                    onChange={(e) => setNewPost(prev => ({ ...prev, content: e?.target?.value }))}
                    placeholder="What's on your heart?"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button type="button" className="p-2 hover:bg-gray-100 rounded-lg">
                        <Image className="w-4 h-4 text-gray-500" />
                      </button>
                      <button type="button" className="p-2 hover:bg-gray-100 rounded-lg">
                        <Paperclip className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        type="button"
                        onClick={() => setShowCreatePost(false)}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        disabled={!newPost?.content?.trim()}
                        className="bg-[#4B3381] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#4B3381]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Post
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Crisis Support Banner */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-4 mb-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Need immediate support? Contact our pastoral care team Via Our{' '}
              <a 
                href="https://t.me/christfaculty" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline font-semibold"
              >
                Telegram Channel
              </a>
              .
            </p>
          </div>
        </div>
      </div>
      {/* Floating Action Button */}
      <button 
        onClick={() => setActiveTab('ask-pastor')}
        className="fixed bottom-6 right-6 bg-[#4B3381] text-white p-4 rounded-full shadow-lg hover:bg-[#4B3381]/90 transition-colors z-40"
        title="Ask Pastor"
      >
        <Bot className="w-6 h-6" />
      </button>
    </div>
  );
};

export default CommunityHub;